/**
 * RN license-number input rules.
 *
 * Pure helpers, no React and no imports, so the unit tests can load this
 * without pulling in the React Native runtime.
 *
 * These mirror the server (`packages/api/src/services/nysed-crawler.ts`):
 * `normalizeLicense` uppercases and strips punctuation, and
 * `isSearchableLicenseNumber` is a LENGTH-ONLY check — letters are legal.
 * The client must not be stricter than the crawler it feeds.
 */

/**
 * Trim and uppercase. Deliberately does NOT strip letters.
 *
 * The client used to send `value.replace(/\D/g, "")`. NYSED issues licenses
 * with a leading letter — 43 nurses in production hold one — so "N49385"
 * was silently sent as "49385": five characters, which NYSED's search form
 * refuses to run, so the nurse got "Please enter a valid 6-digit license
 * number" for a license number she had typed correctly.
 */
export function normalizeLicenseInput(value: string): string {
    return value.trim().toUpperCase();
}

/**
 * Mirrors the server's `isSearchableLicenseNumber`: at least 6 characters
 * after normalizing, letters included. NYSED blocks the search client-side
 * below 6, so anything shorter times out rather than returning "no records".
 */
export function isSearchableLicense(value: string): boolean {
    return normalizeLicenseInput(value).length >= 6;
}

/**
 * Re-exported so the licence field keeps a single import. The rule is not
 * licence-specific — email and the selfie strand on the same spinner — so it
 * lives in `verification-state.ts`.
 */
export { STALE_VERIFY_MS, isVerificationStale } from "./verification-state";
